(() => {
  'use strict';
  if (customElements.get('product-controller')) return;
  const H = window.Habicat;
  if (!H) return;
  const initialState = element => JSON.parse(element.querySelector('[data-variant-initial]')?.textContent || '{"variant":null}');
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController(); this.state = initialState(this); this.pending = false; this.failed = false;
      this.addEventListener('change', event => { if (event.target.matches('[data-option-id]')) this.select(); }, { signal: this.abort.signal });
    }
    disconnectedCallback() { this.abort?.abort(); this.request?.abort(); this.abort = null; }
    async select() {
      this.request?.abort();
      const request = new AbortController(); this.request = request;
      this.pending = true; this.failed = false;
      const error = this.querySelector('.picker-error'); error.hidden = true;
      this.dispatchEvent(new CustomEvent('variant:pending', { bubbles: true }));
      const ids = [...this.querySelectorAll('[data-option-id]:checked')].map(input => input.dataset.optionId);
      const url = new URL(this.dataset.productUrl, location.origin);
      url.searchParams.set('section_id', 'variant-state'); url.searchParams.set('option_values', ids.join(','));
      try {
        const response = await fetch(url, { signal: request.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error(H.config.strings.variantError);
        const json = H.parseHTML(await response.text()).querySelector('[data-variant-response]');
        if (!json) throw new Error(H.config.strings.variantError);
        const state = JSON.parse(json.textContent);
        if (this.request !== request) return;
        this.state = state;
        this.querySelectorAll('.option-group').forEach(group => {
          const selected = group.querySelector('input:checked');
          group.querySelector('[data-option-label]').textContent = selected?.value || '';
          const option = state.options.find(item => String(item.position) === group.dataset.position);
          group.querySelectorAll('[data-option-id]').forEach(input => {
            const value = option?.values.find(item => String(item.id) === input.dataset.optionId);
            if (!value) return;
            input.closest('label').classList.toggle('is-unavailable', !value.available);
            input.closest('label').querySelector('[data-option-note]').hidden = value.available;
          });
        });
      } catch (failure) {
        if (failure.name === 'AbortError') return;
        this.failed = true;
        error.textContent = H.config.strings.variantError; error.hidden = false;
      } finally {
        if (this.request === request && !request.signal.aborted) {
          this.pending = false;
          this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { state: this.state, index: Number(this.dataset.index) } }));
        }
      }
    }
  }
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController(); const signal = this.abort.signal;
      this.track = this.querySelector('[data-gallery-track]'); this.slides = [...this.querySelectorAll('[data-media-id]')];
      this.index = 0; this.raf = 0;
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-gallery-to],[data-gallery-prev],[data-gallery-next]');
        if (!button) return; event.preventDefault();
        const next = button.hasAttribute('data-gallery-to') ? Number(button.dataset.galleryTo) : this.index + (button.hasAttribute('data-gallery-prev') ? -1 : 1);
        this.go(next);
      }, { signal });
      this.track.addEventListener('scroll', () => {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => { this.raf = 0; this.index = Math.round(this.track.scrollLeft / (this.track.clientWidth || 1)); this.paint(); });
      }, { signal, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        if (['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) {
          event.preventDefault();
          this.go(event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      }, { signal });
      this.paint();
    }
    disconnectedCallback() { this.abort?.abort(); this.abort = null; cancelAnimationFrame(this.raf); }
    go(index, smooth = true) {
      this.index = Math.max(0, Math.min(index, this.slides.length - 1));
      this.track.scrollTo({ left: this.index * this.track.clientWidth, behavior: smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant' });
      this.paint();
    }
    selectMedia(id) { const index = this.slides.findIndex(slide => slide.dataset.mediaId === String(id)); if (index >= 0) this.go(index); }
    paint() {
      const prev = this.querySelector('[data-gallery-prev]'); if (prev) prev.disabled = this.index === 0;
      const next = this.querySelector('[data-gallery-next]'); if (next) next.disabled = this.index === this.slides.length - 1;
      this.querySelector('[data-gallery-current]').textContent = String(this.index + 1);
      const progress = this.querySelector('[data-gallery-progress]'); if (progress) progress.style.transform = `translateX(${this.index * 100}%)`;
      this.querySelectorAll('[data-gallery-to]').forEach((link, i) => { if (i === this.index) link.setAttribute('aria-current', 'true'); else link.removeAttribute('aria-current'); });
      this.slides.forEach((slide, i) => { if (i !== this.index) slide.querySelectorAll('video').forEach(video => video.pause()); });
    }
  }
  class ProductController extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController(); const signal = this.abort.signal;
      this.form = this.querySelector('.product-form');
      this.pickers = [...this.querySelectorAll('variant-picker')];
      this.busy = false;
      this.quantity = Number(this.querySelector('[data-bundle]:checked')?.value || this.form.elements.quantity?.value || 1);
      this.form.addEventListener('submit', event => this.submit(event), { signal });
      this.addEventListener('variant:pending', () => this.sync(), { signal });
      this.addEventListener('variant:change', event => {
        if (event.detail.index === 1 && !event.target.failed) {
          const variant = event.detail.state.variant;
          if (variant?.mediaId) this.querySelector('media-gallery')?.selectMedia(variant.mediaId);
          if (this.dataset.updateUrl === 'true') { const url = new URL(location.href); if (variant) url.searchParams.set('variant', variant.id); else url.searchParams.delete('variant'); url.searchParams.delete('option_values'); history.replaceState({}, '', url); }
        }
        this.sync();
      }, { signal });
      this.addEventListener('change', event => {
        if (event.target.matches('[data-bundle]')) this.quantity = Number(event.target.value);
        if (event.target.matches('[name="quantity"]') && !this.querySelector('[data-bundle]')) this.quantity = Math.max(1, Number(event.target.value) || 1);
        if (event.target.matches('[data-bundle],[name="quantity"],[data-upsell-toggle],[data-upsell-variant]')) this.sync();
      }, { signal });
      if (this.querySelector('.sticky-add')) document.body.classList.add('has-sticky-product');
      const media = this.pickers[0]?.state?.variant?.mediaId;
      if (media) this.querySelector('media-gallery')?.selectMedia(media);
      this.sync();
    }
    disconnectedCallback() { this.abort?.abort(); this.abort = null; if (!document.querySelector('product-controller .sticky-add')) document.body.classList.remove('has-sticky-product'); }
    selections(quantity = this.quantity) {
      const multiple = Boolean(this.querySelector('[data-bundle]'));
      if (!multiple) return [{ state: this.pickers[0]?.state || initialState(this.pickers[0]), quantity }];
      return this.pickers.slice(0, quantity).map(picker => ({ state: picker.state || initialState(picker), quantity: 1 }));
    }
    upsells(quantity = this.quantity) {
      return [...this.querySelectorAll('[data-upsell-toggle]:checked')].map(toggle => {
        const selected = toggle.closest('.upsell').querySelector('[data-upsell-variant]')?.selectedOptions[0];
        return { id: Number(selected?.value), available: selected?.dataset.available === 'true', price: Number(selected?.dataset.price || 0), compare: Number(selected?.dataset.compare || 0), quantity: toggle.dataset.perBasket === 'true' ? quantity : 1 };
      });
    }
    total(quantity = this.quantity) {
      let price = 0, compare = 0;
      this.selections(quantity).forEach(({ state, quantity: count }) => { price += (state.variant?.price || 0) * count; compare += Math.max(state.variant?.compare || 0, state.variant?.price || 0) * count; });
      this.upsells(quantity).forEach(item => { price += item.price * item.quantity; compare += Math.max(item.compare, item.price) * item.quantity; });
      return { price, compare };
    }
    items() {
      const items = new Map();
      const add = (id, quantity) => { if (!id) return; const key = String(id); items.set(key, { id: Number(id), quantity: Number(quantity) + (items.get(key)?.quantity || 0) }); };
      this.selections().forEach(({ state, quantity }) => add(state.variant?.id, quantity));
      this.upsells().forEach(item => add(item.id, item.quantity));
      return [...items.values()];
    }
    sync() {
      const bundle = Boolean(this.querySelector('[data-bundle]'));
      this.dataset.quantity = String(this.quantity);
      if (bundle) {
        this.form.elements.quantity.value = this.quantity;
        const extras = this.querySelector('.bundle-extras'); if (extras) extras.classList.toggle('is-multiple', this.quantity > 1);
        this.pickers.forEach((picker, i) => { picker.hidden = i >= this.quantity; picker.querySelectorAll('input').forEach(input => { input.disabled = i >= this.quantity; }); });
      }
      this.pickers.forEach(picker => { const summary = picker.querySelector('[data-basket-summary]'); if (summary) summary.textContent = ' · ' + [...picker.querySelectorAll('.option-group')].sort((a, b) => Number(b.classList.contains('option-group--color')) - Number(a.classList.contains('option-group--color'))).map(group => group.querySelector('[data-option-id]:checked')?.value).filter(Boolean).join(', '); });
      const active = bundle ? this.pickers.slice(0, this.quantity) : this.pickers.slice(0, 1);
      const waiting = active.some(picker => picker.pending || picker.failed);
      const selections = this.selections();
      let valid = selections.length > 0 && selections.every(({ state }) => state.variant?.available) && this.upsells().every(item => item.available && item.id);
      const items = this.items();
      selections.forEach(({ state }) => {
        const variant = state.variant;
        if (!variant) return;
        const amount = items.find(item => item.id === variant.id)?.quantity || 0;
        if (amount < variant.min || (variant.max && amount > variant.max) || (amount - variant.min) % variant.step !== 0) valid = false;
      });
      const variant = selections[0]?.state.variant;
      const id = this.querySelector('[data-variant-id]'); id.value = variant?.id || ''; id.disabled = !valid || waiting;
      const totals = this.total();
      this.querySelectorAll('[data-total-price],[data-sticky-price]').forEach(node => { node.textContent = H.money(totals.price); });
      const compare = this.querySelector('[data-compare-price]'); compare.hidden = totals.compare <= totals.price; compare.textContent = H.money(totals.compare);
      const sale = this.querySelector('[data-sale-badge]'); sale.hidden = totals.compare <= totals.price; sale.textContent = totals.compare > totals.price ? `−${Math.round((1 - totals.price / totals.compare) * 100)}%` : '';
      this.querySelectorAll('[data-bundle-price]').forEach(node => {
  const custom = Number(node.dataset.bundleCustomPrice || 0);
  const total = this.total(Number(node.dataset.bundlePrice));
  if (!custom) node.textContent = H.money(total.price);
});
      this.querySelectorAll('[data-bundle-compare]').forEach(node => { const total = this.total(Number(node.dataset.bundleCompare)); node.hidden = total.compare <= total.price; node.textContent = H.money(total.compare); });
      this.querySelectorAll('[data-upsell-price]').forEach(node => {
        const box = node.closest('.upsell'); const option = box.querySelector('select').selectedOptions[0];
        const quantity = box.querySelector('[data-upsell-toggle]').dataset.perBasket === 'true' ? this.quantity : 1;
        node.textContent = '+' + H.money(Number(option?.dataset.price || 0) * quantity);
        const compare = box.querySelector('[data-upsell-compare]');
        if (compare) { compare.hidden = Number(option?.dataset.compare || 0) <= Number(option?.dataset.price || 0); compare.textContent = H.money(Number(option?.dataset.compare || 0) * quantity); }
      });
      const unit = this.querySelector('[data-unit-price]'); if (unit) { unit.hidden = !variant?.unitPrice; unit.textContent = variant?.unitPrice ? `${variant.unitPrice} / ${variant.unitReference}` : ''; }
      const stock = this.querySelector('[data-stock-threshold]'); if (stock) { const inventory = variant?.inventory; stock.hidden = !inventory || inventory > Number(stock.dataset.stockThreshold); if (!stock.hidden) stock.textContent = (H.config.strings.lowStock || '__COUNT__').replace('__COUNT__', String(inventory)); }
      this.querySelectorAll('[data-product-submit],[data-sticky-submit]').forEach(button => { button.disabled = !valid || waiting || this.busy; });
      const label = this.querySelector('[data-add-label]'); label.textContent = valid ? H.config.strings.add : selections.some(({ state }) => state.variant && !state.variant.available) ? H.config.strings.soldOut : H.config.strings.unavailable;
      const addPrice = this.querySelector('[data-add-price]'); addPrice.textContent = valid ? ` · ${H.money(totals.price)}` : '';
      const stickyVariant = this.querySelector('[data-sticky-variant]'); if (stickyVariant) stickyVariant.textContent = this.quantity > 1 ? 'Bundle' : variant?.title === 'Default Title' ? '' : variant?.title || '';
      const stickyQty = this.querySelector('[data-sticky-quantity]'); if (stickyQty) stickyQty.textContent = String(this.quantity);
      this.valid = valid && !waiting;
    }
    async submit(event) {
      event.preventDefault();
      if (this.busy || !this.valid || !this.form.reportValidity()) return;
      this.busy = true; this.classList.add('is-product-busy'); this.setAttribute('aria-busy','true'); this.sync();
      const error = this.querySelector('[data-product-error]'); error.hidden = true;
      try {
        const items = this.items();
        const formData = new FormData(this.form);
        const properties = {};
        for (const [key, value] of formData) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && typeof value === 'string' && value) properties[match[1]] = value;
        }
        if (Object.keys(properties).length) items.filter(item => this.selections().some(({state}) => state.variant?.id === item.id)).forEach(item => { item.properties = properties; });
        const plan = formData.get('selling_plan');
        if (plan) items.filter(item => this.selections().some(({state}) => state.variant?.id === item.id)).forEach(item => { item.selling_plan = Number(plan); });
        const gifts = [...new Set([...this.querySelectorAll('[data-gift-id]')].map(node => Number(node.dataset.giftId)))];
        if (gifts.length) {
          const cart = await H.getCart();
          gifts.forEach(id => { if (!cart.items.some(item => item.variant_id === id) && !items.some(item => item.id === id)) items.push({ id, quantity: 1, properties: { _habicat_gift: 'true' } }); });
        }
        await H.mutateCart(H.config.add, { items }, { add: true, open: H.config.drawer, trigger: event.submitter });
        if (!H.config.drawer) location.assign(H.config.cart);
      } catch (failure) { error.textContent = failure.message || H.config.strings.error; error.hidden = false; error.tabIndex = -1; error.focus(); }
      finally { this.busy = false; this.classList.remove('is-product-busy'); this.removeAttribute('aria-busy'); this.sync(); }
    }
  }
  customElements.define('variant-picker', VariantPicker);
  customElements.define('media-gallery', MediaGallery);
  customElements.define('product-controller', ProductController);
})();
